import React, { useEffect, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { AnalogClock } from '../../../visuals/components/AnalogClock';
import { Clock, Sun, Moon } from 'lucide-react';
import { error as logError } from '../../../utils/logger';
export function ClockRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  // CANONICAL: Read ONLY from data.clock (no legacy fallback)
  const clockConfig = question.data?.clock as any;
  const prevValidateRef = useRef(false);
  if (!clockConfig || !clockConfig.target) {
    logError('ClockRenderer: Missing clock config in question.data.clock');
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-4857">
        <p className="text-red-700 font-bold" data-id="element-4858">⚠️ Klok configuratie ontbreekt</p>
        <p className="text-sm text-red-600 mt-2" data-id="element-4859">
          Deze vraag heeft geen geldige klok data.
        </p>
      </div>;
  }
  const targetHours = clockConfig.target.hours || 0;
  const targetMinutes = clockConfig.target.minutes || 0;
  const targetSeconds = 0; // Seconds not supported yet
  const format = clockConfig.target.format || '24h';
  // Determine clock mode (analog vs digital)
  const mode = clockConfig.mode || 'read_analog_fill'; // Default to analog
  const showDigitalClock = mode === 'read_digital_fill';
  // Determine time of day for visual indicator
  const timeOfDay = targetHours >= 6 && targetHours < 18 ? 'day' : 'night';
  const TimeIcon = timeOfDay === 'day' ? Sun : Moon;
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  // Auto-focus first input field
  const hoursInputRef = useRef<HTMLInputElement>(null);
  // Reset state when question changes
  useEffect(() => {
    setHours('');
    setMinutes('');
    setSeconds('');
    prevValidateRef.current = false;
    // Auto-focus hours input on mount
    if (hoursInputRef.current) {
      hoursInputRef.current.focus();
    }
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    const isReady = hours !== '' && minutes !== '' && (targetSeconds === 0 ? true : seconds !== '');
    onReadyChange?.(isReady);
  }, [hours, minutes, seconds, targetSeconds, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const h = hours.padStart(2, '0');
      const m = minutes.padStart(2, '0');
      const s = targetSeconds !== 0 ? seconds.padStart(2, '0') : '00';
      const answerStr = `${h}:${m}:${s}`;
      onAnswer(answerStr);
    }
  }, [shouldValidate, hours, minutes, seconds, targetSeconds, onAnswer]);
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string, max: number) => {
    if (value === '' || /^\d+$/.test(value) && parseInt(value) <= max) {
      setter(value);
    }
  };
  const showSeconds = targetSeconds !== 0;
  // Format time for digital display
  const formatDigitalTime = () => {
    const h = targetHours.toString().padStart(2, '0');
    const m = targetMinutes.toString().padStart(2, '0');
    return `${h}:${m}`;
  };
  return <div className="flex flex-col items-center w-full max-w-2xl mx-auto space-y-6" data-id="element-4860">
      {/* Time of Day Indicator */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-3 rounded-2xl border-2 border-indigo-200 shadow-sm" data-id="element-4861">
        <TimeIcon className={`w-6 h-6 ${timeOfDay === 'day' ? 'text-amber-500' : 'text-indigo-400'}`} data-id="element-4862" />

        <span className="text-base font-bold text-slate-700" data-id="element-4863">
          {timeOfDay === 'day' ? 'Overdag (06:00 - 18:00)' : "'s Nachts (18:00 - 06:00)"}
        </span>
      </div>

      {/* Clock Display - Analog or Digital */}
      {showDigitalClock ? <div className="p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl border-4 border-slate-700 shadow-2xl" data-id="element-4864">
          <div className="font-mono text-6xl font-black text-emerald-400 tracking-wider" data-id="element-4865">
            {formatDigitalTime()}
          </div>
        </div> : <div className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl border-2 border-slate-200 shadow-xl" data-id="element-4866">
          <AnalogClock hours={targetHours} minutes={targetMinutes} seconds={targetSeconds} showSeconds={showSeconds} size={180} data-id="element-4867" />
        </div>}

      {/* Time Input */}
      <div className="w-full bg-white p-6 rounded-2xl border-2 border-indigo-100 shadow-lg" data-id="element-4868">
        <div className="flex items-center justify-between mb-5" data-id="element-4869">
          <div className="flex items-center gap-2" data-id="element-4870">
            <div className="p-2 bg-indigo-50 rounded-xl" data-id="element-4871">
              <Clock className="w-5 h-5 text-indigo-600" data-id="element-4872" />
            </div>
            <span className="text-sm font-bold text-slate-500 uppercase tracking-wide" data-id="element-4873">
              Vul de tijd in
            </span>
          </div>
          <div className="px-3 py-1.5 bg-indigo-50 rounded-full border border-indigo-200" data-id="element-4874">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide" data-id="element-4875">
              {format === '24h' ? '24-uurs' : '12-uurs'}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3" data-id="element-4876">
          {/* Hours Input */}
          <div className="flex flex-col items-center" data-id="element-4877">
            <label className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide" data-id="element-4878">
              Uur
            </label>
            <input ref={hoursInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={hours} onChange={e => handleInputChange(setHours, e.target.value, 23)} placeholder="00" className="w-16 h-16 text-center text-2xl font-black rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300 bg-slate-50" maxLength={2} data-id="element-4879" />
          </div>

          <div className="text-3xl font-black text-slate-400 mt-8" data-id="element-4880">:</div>

          {/* Minutes Input */}
          <div className="flex flex-col items-center" data-id="element-4881">
            <label className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide" data-id="element-4882">
              Min
            </label>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={minutes} onChange={e => handleInputChange(setMinutes, e.target.value, 59)} placeholder="00" className="w-16 h-16 text-center text-2xl font-black rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300 bg-slate-50" maxLength={2} data-id="element-4883" />
          </div>

          {/* Seconds Input (Conditional) */}
          {showSeconds && <>
              <div className="text-3xl font-black text-slate-400 mt-8" data-id="element-4884">:</div>
              <div className="flex flex-col items-center" data-id="element-4885">
                <label className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide" data-id="element-4886">
                  Sec
                </label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={seconds} onChange={e => handleInputChange(setSeconds, e.target.value, 59)} placeholder="00" className="w-16 h-16 text-center text-2xl font-black rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300 bg-slate-50" maxLength={2} data-id="element-4887" />
              </div>
            </>}
        </div>

        <p className="text-center text-slate-500 text-sm mt-4 font-medium" data-id="element-4888">
          Typ de tijd zoals je die op de klok ziet
        </p>
      </div>
    </div>;
}